import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EMPTY_PLATE, PlateParts, formatPlate, isSerialTooLong, parsePlate } from 'config/plateNumber';

interface PlateNumberInputProps {
  /** 最初の欄（地名）の id。欄の名前（label の htmlFor）と結ぶ。 */
  id: string;
  /** 保存の文字列（「品川 330 な 38-59」）。 */
  value: string;
  /** 4つの欄から組み立てた文字列。欄が欠けていれば空。 */
  onChange: (value: string) => void;
  ariaRequired?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

const PARTS: { key: keyof PlateParts; className: string; maxLength?: number; inputMode?: 'numeric' }[] = [
  { key: 'area', className: 'w-28', maxLength: 8 },
  { key: 'classNumber', className: 'w-20', maxLength: 3 },
  { key: 'kana', className: 'w-14 text-center', maxLength: 1 },
  // #475 の自動レビュー（P2）。一連番号は4桁までの数字（ハイフンは組み立てるときに付ける）。
  // 欄の maxLength では切らない（2件目の指摘）。「38-59」を貼り付けるとブラウザが「38-5」に切り、
  // 「・385」で保存していた。受け取った全体から数字を取り出して4桁以内かを確かめる（config/plateNumber）。
  { key: 'serial', className: 'w-24', inputMode: 'numeric' },
];

/**
 * A07（2026-11-03 和田様の UAT）。車両ナンバーを地名・分類番号・ひらがな・一連番号の4つの欄で入れる。
 *
 * 組み立て・分け方は config/plateNumber。分けられない既存の値（自由入力の頃の値）は、欄を書き換えるまで
 * 元の文字列のまま残し、欄の下に今の値を出す。
 */
const PlateNumberInput: React.FC<PlateNumberInputProps> = ({
  id,
  value,
  onChange,
  ariaRequired,
  invalid,
  describedBy,
}) => {
  const { t } = useTranslation('vehicle');
  const [parts, setParts] = useState<PlateParts>(() => parsePlate(value) ?? EMPTY_PLATE);
  // 最後にこの欄から渡した値。外から別の値が来たとき（開き直し・最新の値の読み直し）だけ分け直す。
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setParts(parsePlate(value) ?? EMPTY_PLATE);
  }, [value]);

  // 分けられない既存の値（まだ欄を書き換えていない）。
  const legacy = Boolean(value) && parsePlate(value) === null && value === emitted.current;

  const handlePart = (key: keyof PlateParts, text: string) => {
    const next = { ...parts, [key]: text };
    setParts(next);
    const assembled = formatPlate(next);
    emitted.current = assembled;
    onChange(assembled);
  };

  const preview = formatPlate(parts);
  // 貼り付けなどで4桁を超えたら、保存させず（値は空）、欄の下に理由を出す。
  const serialTooLong = isSerialTooLong(parts.serial);
  const serialErrorId = `${id}-serial-error`;

  return (
    <div>
      <div className="flex flex-row flex-wrap items-start gap-2 mt-1">
        {PARTS.map((part, index) => (
          <label key={part.key} className="flex flex-col text-caption text-neutral/100">
            <input
              type="text"
              id={index === 0 ? id : undefined}
              name={`plate_${part.key}`}
              className={`${part.className} p-2 border ${
                invalid ? 'border-red-500' : 'border-neutral/50'
              } text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-3`}
              aria-label={index === 0 ? undefined : t(`all-vehicle.plate.${part.key}`)}
              aria-required={ariaRequired ? 'true' : undefined}
              aria-invalid={invalid || (part.key === 'serial' && serialTooLong) ? 'true' : undefined}
              aria-describedby={
                part.key === 'serial' && serialTooLong
                  ? [describedBy, serialErrorId].filter(Boolean).join(' ')
                  : describedBy
              }
              placeholder={t(`all-vehicle.plate.${part.key}-example`)}
              maxLength={part.maxLength}
              inputMode={part.inputMode}
              value={parts[part.key]}
              onChange={(event) => handlePart(part.key, event.target.value)}
            />
            <span aria-hidden={index === 0 ? undefined : 'true'}>{t(`all-vehicle.plate.${part.key}`)}</span>
          </label>
        ))}
      </div>
      {serialTooLong ? (
        <p id={serialErrorId} role="alert" className="mt-1 text-caption text-error">
          {t('all-vehicle.plate.serial-too-long')}
        </p>
      ) : null}
      {legacy ? (
        <p className="mt-1 text-caption text-neutral/100" data-testid="plate-legacy">
          {t('all-vehicle.plate.legacy', { value })}
        </p>
      ) : preview ? (
        <p className="mt-1 text-caption text-neutral/100" data-testid="plate-preview">
          {t('all-vehicle.plate.preview', { value: preview })}
        </p>
      ) : null}
    </div>
  );
};

export default PlateNumberInput;
